import { ApiError } from '@api/api-client';
import { importCategories, type ImportCategoryRow, type ImportResult } from '@api/categories-api';
import Modal from '@components/modal';
import {
  extractLocales,
  parseCategoryRows,
  validateCategoryHeaders,
} from '@utils/csv-category-parser';
import { parseCsvHeaders } from '@utils/csv-utils';
import { AlertTriangle, CheckCircle, FileUp, Upload, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { sileo } from 'sileo';

interface ImportCsvModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ImportState = 'idle' | 'preview' | 'importing' | 'done';

export default function ImportCsvModal({
  isOpen,
  onClose,
}: ImportCsvModalProps): React.JSX.Element {
  const [state, setState] = useState<ImportState>('idle');
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState<ImportCategoryRow[]>([]);
  const [detectedLocales, setDetectedLocales] = useState<string[]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback((): void => {
    setState('idle');
    setFileName('');
    setParsedRows([]);
    setDetectedLocales([]);
    setResult(null);
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const processFile = useCallback((file: File): void => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.tsv') && !file.name.endsWith('.txt')) {
      sileo.error({ title: 'Invalid file type. Please upload a CSV/TSV file.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;

      const validationError = validateCategoryHeaders(content);
      if (validationError) {
        sileo.error({ title: validationError });
        return;
      }

      const rows = parseCategoryRows(content);
      if (rows.length === 0) {
        sileo.error({ title: 'No valid data rows found in the file.' });
        return;
      }

      const { headers } = parseCsvHeaders(content.split(/\r?\n/)[0]!);
      setDetectedLocales(extractLocales(headers));
      setParsedRows(rows);
      setFileName(file.name);
      setState('preview');
    };
    reader.readAsText(file);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleImport = async (): Promise<void> => {
    setState('importing');
    try {
      const importResult = await importCategories(parsedRows);
      setResult(importResult);
      setState('done');

      if (importResult.errors.length === 0) {
        sileo.success({
          title: `Import complete: ${importResult.created} created, ${importResult.updated} updated`,
        });
      } else {
        sileo.warning({
          title: `Import finished with ${importResult.errors.length} warning(s)`,
        });
      }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Import failed';
      sileo.error({ title: message });
      setState('preview');
    }
  };

  const renderIdle = (): React.JSX.Element => (
    <div
      className={`csv-drop-zone${isDragging ? ' csv-drop-zone--active' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
      }}
    >
      <Upload size={40} className="csv-drop-zone__icon" />
      <p className="csv-drop-zone__title">Drag & drop your CSV file here</p>
      <p className="csv-drop-zone__subtitle">or click to browse. Supported: .csv, .tsv, .txt</p>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.tsv,.txt"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </div>
  );

  const renderPreview = (): React.JSX.Element => (
    <div className="csv-preview">
      <div className="csv-file-info">
        <FileUp size={20} className="csv-file-info__icon" />
        <div className="csv-file-info__details">
          <p className="csv-file-info__name">{fileName}</p>
          <p className="csv-file-info__meta">
            {parsedRows.length} categor{parsedRows.length !== 1 ? 'ies' : 'y'} found
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-icon btn-sm"
          onClick={reset}
          aria-label="Remove file"
        >
          <X size={16} />
        </button>
      </div>

      <div className="csv-preview__locales">
        <span className="csv-preview__locales-label">Detected locales:</span>
        {detectedLocales.map((locale) => (
          <span key={locale} className="badge badge-draft">
            {locale}
          </span>
        ))}
      </div>

      <div className="csv-preview__table-wrapper">
        <table className="csv-preview__table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Clinic</th>
              <th>Global</th>
              <th>Translations</th>
            </tr>
          </thead>
          <tbody>
            {parsedRows.map((row) => (
              <tr key={row.externalId}>
                <td>{row.baseName}</td>
                <td>{row.isClinic ? 'Yes' : 'No'}</td>
                <td>{row.isGlobal === undefined ? 'Unchanged' : row.isGlobal ? 'Yes' : 'No'}</td>
                <td>{row.details.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDone = (): React.JSX.Element | null => {
    if (!result) return null;

    const hasErrors = result.errors.length > 0;

    return (
      <div className="csv-result">
        <div
          className={`csv-result__banner csv-result__banner--${hasErrors ? 'warning' : 'success'}`}
        >
          {hasErrors ? (
            <AlertTriangle size={24} className="csv-result__banner-icon--warning" />
          ) : (
            <CheckCircle size={24} className="csv-result__banner-icon--success" />
          )}
          <p className="csv-result__banner-title">
            {hasErrors ? 'Import completed with warnings' : 'Import completed successfully'}
          </p>
        </div>

        <div className="csv-result__stats csv-result__stats--3">
          <div className="csv-result__stat">
            <p className="csv-result__stat-value csv-result__stat-value--primary">
              {result.created}
            </p>
            <p className="csv-result__stat-label">Created</p>
          </div>
          <div className="csv-result__stat">
            <p className="csv-result__stat-value csv-result__stat-value--info">{result.updated}</p>
            <p className="csv-result__stat-label">Updated</p>
          </div>
          <div className="csv-result__stat">
            <p className="csv-result__stat-value csv-result__stat-value--muted">{result.skipped}</p>
            <p className="csv-result__stat-label">Skipped</p>
          </div>
        </div>

        {hasErrors && (
          <div className="csv-result__warnings">
            <p className="csv-result__warnings-title">Warnings:</p>
            <ul className="csv-result__warnings-list">
              {result.errors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        state === 'done'
          ? 'Import results'
          : state === 'preview'
            ? 'Review import data'
            : 'Import categories from CSV'
      }
      footer={
        state === 'done' ? (
          <button type="button" className="btn btn-primary" onClick={handleClose}>
            Close
          </button>
        ) : state === 'preview' ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={reset}>
              Back
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void handleImport()}>
              <Upload size={16} />
              Import {parsedRows.length} categor{parsedRows.length !== 1 ? 'ies' : 'y'}
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-secondary" onClick={handleClose}>
            Cancel
          </button>
        )
      }
    >
      {state === 'idle' && renderIdle()}
      {state === 'preview' && renderPreview()}
      {state === 'importing' && (
        <div className="csv-importing">
          <p className="csv-importing__title">Importing categories...</p>
          <p className="csv-importing__subtitle">This may take a moment</p>
        </div>
      )}
      {state === 'done' && renderDone()}
    </Modal>
  );
}
